/**
 * One team member, edited in groups: Member, Runs, Looks, Instructions, Advanced and (for an agent
 * the platform knows) Identity. Settings stay calm: a plain row per field with its words on the
 * left, at most three controls in a row, a quiet "last changed by" note where the platform has one.
 * The lists (roles, runtimes, models, thinking levels) come from the vocabulary.
 */

import { type Agent365View, CLOUD_PROVIDER_IDS, MEMBER_PERMISSION_MODES, type CloudProviderId, type OrgTeamMemberV2, type SentinelView } from '@aioe/shared';
import { Segment } from '@heroui-pro/react';
import { type ReactNode, useState } from 'react';

import type { TeamVocabulary } from '../../api/client.ts';
import { timeAgo } from '../../hooks.ts';
import { CLOUD_PROVIDER_TEXT } from '../policyEditor.tsx';
import { SubjectName, SubjectPicker } from '../SubjectPicker.tsx';
import { Button, ChoiceSelect, inputClass, StatusMark, Tick } from '../ui.tsx';
import { CharacterPicker, MemberFace, pictureMode } from './MemberFace.tsx';
import { PERMISSION_WORDS, THINKING_WORDS, sentence } from './teamsModel.ts';

/** A ListBox id for the empty answer: an empty string is not a reliable id. */
const NONE = '__none';

/** What the editor looks things up in: loaded once by the team editor and shared by its members. */
export interface MemberLookups {
  vocabulary: TeamVocabulary;
  catalogue: Array<{ id: string; kind: string; name: string }>;
  nodes: Array<{ id: string; name: string }>;
  workbenches: Array<{ id: string; name: string }>;
  specialists: Array<{ id: string; name: string }>;
  agent365?: Agent365View;
  sentinel?: SentinelView;
}

type Group = 'member' | 'runs' | 'looks' | 'instructions' | 'advanced' | 'identity';

/** A plain row: the words at the start, the control (or up to three) filling the rest, a quiet note at the end. */
export function Row({ label, note, hint, children }: { label: string; note?: string; hint?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[11rem_minmax(0,1fr)] sm:items-start sm:gap-4">
      <div className="pt-1.5 text-sm text-muted">{label}</div>
      <div className="grid min-w-0 gap-1">
        <div className="flex flex-wrap items-center gap-2">{children}</div>
        {(hint || note) && (
          <div className="text-xs text-muted">
            {hint}
            {hint && note ? ' · ' : ''}
            {note}
          </div>
        )}
      </div>
    </div>
  );
}

const opt = (rows: Array<{ id: string; name: string }>, none: string): Record<string, string> => ({ [NONE]: none, ...Object.fromEntries(rows.map((r) => [r.id, r.name])) });

export function MemberEditor({
  member,
  onChange,
  lookups,
  teamId,
  note,
  readOnly,
  isLead,
}: {
  member: OrgTeamMemberV2;
  onChange: (patch: Partial<OrgTeamMemberV2>) => void;
  lookups: MemberLookups;
  /** The saved team's id, for the identity block; absent for a team not saved yet. */
  teamId?: string;
  /** The last change to a field of this member: `members.<name>.<field>`. */
  note: (field: string) => string | undefined;
  readOnly?: boolean;
  isLead?: boolean;
}) {
  const [group, setGroup] = useState<Group>('member');
  const m = member;
  const { vocabulary: v } = lookups;
  const skills = lookups.catalogue.filter((e) => e.kind === 'skill');
  const roles = lookups.catalogue.filter((e) => e.kind === 'role');
  const noteOf = (f: string) => (m.name ? note(f) : undefined);
  const set = (patch: Partial<OrgTeamMemberV2>) => onChange(patch);

  // Where it runs: the computer that starts it is never a choice, only what is left blank.
  const place = m.runsOn;
  const mode: 'here' | 'tags' | 'node' | 'workbench' | 'cloud' = place?.node ? 'node' : place?.device ? 'workbench' : place?.cloud ? 'cloud' : place ? 'tags' : 'here';
  const setMode = (next: typeof mode) => {
    const keep = { ...(place?.fallback ? { fallback: place.fallback } : {}) };
    if (next === 'here') return set({ runsOn: undefined });
    if (next === 'tags') return set({ runsOn: { ...keep, tags: place?.tags ?? [] } });
    if (next === 'node') return set({ runsOn: { ...keep, node: lookups.nodes[0]?.id ?? '' } });
    if (next === 'workbench') return set({ runsOn: { ...keep, device: lookups.workbenches[0]?.id ?? '' } });
    return set({ runsOn: { cloud: { provider: CLOUD_PROVIDER_IDS[0]! } } });
  };

  const models = v.models.filter((x) => !m.provider || x.runtime === m.provider);
  const runtimeName = (id: string) => v.runtimes.find((r) => r.id === id)?.name ?? id;
  const tabs: Array<[Group, string]> = m.human
    ? [['member', 'Member'], ['looks', 'Looks'], ['advanced', 'Advanced']]
    : [['member', 'Member'], ['runs', 'Runs'], ['looks', 'Looks'], ['instructions', 'Instructions'], ['advanced', 'Advanced'], ['identity', 'Identity']];
  const shownGroup = tabs.some(([g]) => g === group) ? group : 'member';

  const agentView = teamId ? lookups.agent365?.teams.find((t) => t.id === teamId)?.members.find((x) => x.name === m.name)?.agent : undefined;
  const mode3 = pictureMode(m);

  return (
    <div className="grid grid-cols-1 gap-4">
      {/* Six parts do not fit a phone's width: the row scrolls sideways inside the card rather than widening it (as the project's tabs do). */}
      <div className="-m-1 min-w-0 max-w-full overflow-x-auto p-1">
        <Segment aria-label="Part of the member" size="sm" selectedKey={shownGroup} onSelectionChange={(k) => setGroup(String(k) as Group)} className="w-fit min-w-max">
          {tabs.map(([id, text]) => (
            <Segment.Item key={id} id={id}>
              {text}
            </Segment.Item>
          ))}
        </Segment>
      </div>

      {shownGroup === 'member' && (
        <div className="grid grid-cols-1 gap-3">
          <Row label="Name" note={noteOf('name')} hint={m.formerNames?.length ? `Also known as ${m.formerNames.join(', ')}` : undefined}>
            <input className={`${inputClass} max-w-64`} aria-label="Member name" value={m.name} disabled={readOnly} onChange={(e) => set({ name: e.target.value })} placeholder="Name" />
            <ChoiceSelect label="Agent or person" value={m.human ? 'human' : 'agent'} options={{ agent: 'Agent', human: 'Person' }} isDisabled={readOnly} onChange={(x) => set({ human: x === 'human' })} />
            {isLead && <span className="text-sm text-muted">Leads the team</span>}
          </Row>
          {m.human ? (
            <Row label="Teammate" note={noteOf('subjectId')} hint="Linked to a person in your organisation, so their picture and Azure DevOps name follow them.">
              <span className="text-sm">{m.subjectId ? <SubjectName id={m.subjectId} /> : 'No one chosen yet'}</span>
              {!readOnly && <SubjectPicker kinds={['user']} placeholder="Find the person" className="min-w-56" onPick={(s) => set({ subjectId: s.id, ...(m.name ? {} : { name: s.name }) })} />}
            </Row>
          ) : (
            <>
              <Row label="Role" note={noteOf('role') ?? noteOf('roleKind')} hint="The workbench reads the kind to pick defaults; the words are the team's own.">
                <ChoiceSelect label="Kind of role" value={m.roleKind ?? NONE} isDisabled={readOnly} options={{ [NONE]: 'No kind of role', ...Object.fromEntries(v.roles.map((r) => [r.id, sentence(r.words)])) } as Record<string, string>} onChange={(x) => set({ roleKind: x === NONE ? undefined : (x as OrgTeamMemberV2['roleKind']) })} />
                <input className={`${inputClass} max-w-64`} aria-label="Role in the team's words" value={m.role ?? ''} disabled={readOnly} onChange={(e) => set({ role: e.target.value || undefined })} placeholder="Backend developer" />
              </Row>
              {roles.length > 0 && (
                <Row label="Catalogue role" note={noteOf('roleId')} hint="Its instructions are delivered with the member.">
                  <ChoiceSelect label="Catalogue role" value={m.roleId ?? NONE} isDisabled={readOnly} options={opt(roles, 'No catalogue role')} onChange={(x) => set({ roleId: x === NONE ? undefined : x })} />
                </Row>
              )}
              <Row label="Skills" note={noteOf('skillIds') ?? noteOf('skills')}>
                <ChoiceSelect
                  label="Add a skill"
                  value={undefined}
                  isDisabled={readOnly}
                  placeholder={m.skillIds.length ? `${m.skillIds.length} from the catalogue` : 'Add a catalogue skill'}
                  options={Object.fromEntries(skills.filter((s) => !m.skillIds.includes(s.id)).map((s) => [s.id, s.name]))}
                  onChange={(x) => set({ skillIds: [...new Set([...m.skillIds, x])] })}
                />
                <input
                  className={`${inputClass} max-w-64`}
                  aria-label="Other skills, by name"
                  value={(m.skills ?? []).join(', ')}
                  disabled={readOnly}
                  onChange={(e) => set({ skills: e.target.value.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 50) })}
                  placeholder="Other skills, separated by commas"
                />
                {m.skillIds.length > 0 && (
                  <ul className="flex basis-full flex-wrap gap-x-3 gap-y-1 text-sm">
                    {m.skillIds.map((s) => (
                      <li key={s} className="inline-flex items-center gap-1">
                        {skills.find((x) => x.id === s)?.name ?? s}
                        {!readOnly && (
                          <button type="button" className="text-muted hover:text-foreground" aria-label={`Remove ${skills.find((x) => x.id === s)?.name ?? s}`} onClick={() => set({ skillIds: m.skillIds.filter((x) => x !== s) })}>
                            ×
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Row>
              {lookups.specialists.length > 0 && (
                <Row label="Specialist" note={noteOf('ref')} hint="Uses that specialist's own definition and identity rather than a second one.">
                  <ChoiceSelect label="The specialist it is" value={m.ref ?? NONE} isDisabled={readOnly} options={opt(lookups.specialists, 'Not a specialist')} onChange={(x) => set({ ref: x === NONE ? undefined : x })} />
                </Row>
              )}
            </>
          )}
        </div>
      )}

      {shownGroup === 'runs' && (
        <div className="grid grid-cols-1 gap-3">
          <Row label="Runs on" note={noteOf('runsOn')} hint={mode === 'here' ? 'On the computer that starts it.' : undefined}>
            <ChoiceSelect
              label="Where it runs"
              value={mode}
              isDisabled={readOnly}
              options={{ here: 'The computer that starts it', tags: 'Any machine with these tags', node: 'A node', workbench: "Another person's workbench", cloud: "A provider's cloud" }}
              onChange={setMode}
            />
            {mode === 'tags' && (
              <input className={`${inputClass} max-w-64`} aria-label="Machine tags" value={(place?.tags ?? []).join(', ')} disabled={readOnly} onChange={(e) => set({ runsOn: { ...place, tags: e.target.value.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 16) } })} placeholder="tag:gpu, tag:finance" />
            )}
            {mode === 'node' &&
              (lookups.nodes.length ? (
                <ChoiceSelect label="Node" value={place?.node} isDisabled={readOnly} options={Object.fromEntries(lookups.nodes.map((n) => [n.id, n.name]))} onChange={(x) => set({ runsOn: { ...place, node: x } })} />
              ) : (
                <input className={`${inputClass} max-w-64`} aria-label="Node id" value={place?.node ?? ''} disabled={readOnly} onChange={(e) => set({ runsOn: { ...place, node: e.target.value } })} placeholder="The node's device id" />
              ))}
            {mode === 'workbench' &&
              (lookups.workbenches.length ? (
                <ChoiceSelect label="Workbench" value={place?.device} isDisabled={readOnly} options={Object.fromEntries(lookups.workbenches.map((n) => [n.id, n.name]))} onChange={(x) => set({ runsOn: { ...place, device: x } })} />
              ) : (
                <input className={`${inputClass} max-w-64`} aria-label="Workbench id" value={place?.device ?? ''} disabled={readOnly} onChange={(e) => set({ runsOn: { ...place, device: e.target.value } })} placeholder="The workbench's device id" />
              ))}
            {mode === 'cloud' && (
              <ChoiceSelect
                label="Provider's cloud"
                value={place?.cloud?.provider}
                isDisabled={readOnly}
                options={Object.fromEntries(CLOUD_PROVIDER_IDS.map((id) => [id, CLOUD_PROVIDER_TEXT[id]])) as Record<CloudProviderId, string>}
                onChange={(x) => set({ runsOn: { ...place, cloud: { ...(place?.cloud ?? {}), provider: x } } })}
              />
            )}
          </Row>
          <Row label="Runtime" note={noteOf('provider')} hint="The agent that does the work.">
            {v.runtimes.length ? (
              <ChoiceSelect label="Runtime" value={m.provider ?? NONE} isDisabled={readOnly} options={{ [NONE]: "The workbench's default", ...Object.fromEntries(v.runtimes.map((r) => [r.id, r.name])), ...(m.provider && !v.runtimes.some((r) => r.id === m.provider) ? { [m.provider]: m.provider } : {}) }} onChange={(x) => set({ provider: x === NONE ? undefined : x, model: undefined })} />
            ) : (
              <input className={`${inputClass} max-w-64`} aria-label="Runtime" value={m.provider ?? ''} disabled={readOnly} onChange={(e) => set({ provider: e.target.value || undefined })} placeholder="claude-code" />
            )}
          </Row>
          <Row label="Provider sign-in" note={noteOf('providerId') ?? noteOf('credential') ?? noteOf('account')} hint="Names only: the key itself stays in each machine's own store.">
            <input className={`${inputClass} max-w-44`} aria-label="Provider" value={m.providerId ?? ''} disabled={readOnly} onChange={(e) => set({ providerId: e.target.value || undefined })} placeholder="Provider" />
            <input className={`${inputClass} max-w-44`} aria-label="Provider key, by name" value={m.credential ?? ''} disabled={readOnly} onChange={(e) => set({ credential: e.target.value || undefined })} placeholder="Key name" />
            <input className={`${inputClass} max-w-44`} aria-label="Provider account, by name" value={m.account ?? ''} disabled={readOnly} onChange={(e) => set({ account: e.target.value || undefined })} placeholder="Account name" />
          </Row>
          <Row label="Model" note={noteOf('model')} hint={m.provider && models.length ? `The models ${runtimeName(m.provider)} offers.` : undefined}>
            {models.length ? (
              <ChoiceSelect label="Model" value={m.model ?? NONE} isDisabled={readOnly} options={{ [NONE]: "The runtime's default", ...Object.fromEntries(models.map((x) => [x.id, x.name ?? x.id])), ...(m.model && !models.some((x) => x.id === m.model) ? { [m.model]: m.model } : {}) }} onChange={(x) => set({ model: x === NONE ? undefined : x })} />
            ) : (
              <input className={`${inputClass} max-w-64`} aria-label="Model" value={m.model ?? ''} disabled={readOnly} onChange={(e) => set({ model: e.target.value || undefined })} placeholder="The runtime's default" />
            )}
          </Row>
          <Row label="Thinking level" note={noteOf('thinkingLevel')}>
            <ChoiceSelect label="Thinking level" value={m.thinkingLevel ?? 'default'} isDisabled={readOnly} options={Object.fromEntries(v.thinkingLevels.map((x) => [x, THINKING_WORDS[x] ?? sentence(x)])) as Record<string, string>} onChange={(x) => set({ thinkingLevel: x as OrgTeamMemberV2['thinkingLevel'] })} />
          </Row>
        </div>
      )}

      {shownGroup === 'looks' && (
        <div className="grid grid-cols-1 gap-3">
          <Row label="Picture" note={noteOf('avatar') ?? noteOf('pictureUrl') ?? noteOf('useCharacter')}>
            <MemberFace member={m} />
            <ChoiceSelect
              label="Kind of picture"
              value={mode3}
              isDisabled={readOnly}
              options={(m.human && m.subjectId ? { character: 'A character', address: 'A picture address', organisation: "The organisation's picture" } : { character: 'A character', address: 'A picture address' }) as Record<string, string>}
              onChange={(x: string) => set(x === 'character' ? { useCharacter: true, pictureUrl: undefined, avatar: m.avatar ?? 0 } : x === 'address' ? { useCharacter: false, pictureUrl: m.pictureUrl ?? '' } : { useCharacter: false, pictureUrl: undefined })}
            />
            {mode3 === 'character' && !readOnly && <CharacterPicker value={m.avatar} memberName={m.name || undefined} onChange={(avatar) => set({ avatar, useCharacter: true })} />}
            {mode3 === 'address' && <input className={`${inputClass} max-w-80`} aria-label="Picture address" type="url" value={m.pictureUrl ?? ''} disabled={readOnly} onChange={(e) => set({ pictureUrl: e.target.value || '', useCharacter: false })} placeholder="https://" />}
          </Row>
          <Row label="Colour" note={noteOf('color')} hint="Tints the picture on cards and lanes.">
            <input type="color" aria-label="Colour" className="h-8 w-12 cursor-[var(--cursor-interactive)] rounded border border-field-border bg-field-background p-0.5" value={m.color ?? '#58a6ff'} disabled={readOnly} onChange={(e) => set({ color: e.target.value })} />
            <input className={`${inputClass} max-w-32`} aria-label="Colour, as hex" value={m.color ?? ''} disabled={readOnly} onChange={(e) => set({ color: e.target.value || undefined })} placeholder="#58a6ff" />
            {m.color && !readOnly && (
              <Button size="sm" variant="ghost" onPress={() => set({ color: undefined })}>
                Clear
              </Button>
            )}
          </Row>
        </div>
      )}

      {shownGroup === 'instructions' && (
        <Row label="Agent instructions" note={noteOf('agentInstructions')} hint={`${(m.agentInstructions ?? '').length.toLocaleString('en-AU')} of 32,000 characters. Delivered to the member after the role's and skills' own.`}>
          <textarea className={`${inputClass} min-h-40 resize-y`} aria-label="Agent instructions" rows={8} maxLength={32_000} value={m.agentInstructions ?? ''} disabled={readOnly} onChange={(e) => set({ agentInstructions: e.target.value || undefined })} placeholder="How this member works, what it prefers, what it must never do." />
        </Row>
      )}

      {shownGroup === 'advanced' && (
        <div className="grid grid-cols-1 gap-3">
          {!m.human && (
            <>
              <Row label="Own worktree" note={noteOf('useWorktree')} hint="Works in a separate copy of the repository, so its changes stay apart until merged.">
                <Tick checked={Boolean(m.useWorktree)} isDisabled={readOnly} onChange={(x) => set({ useWorktree: x })}>
                  Give it its own worktree
                </Tick>
              </Row>
              <Row label="Permission mode" note={noteOf('permissionMode')}>
                <ChoiceSelect label="Permission mode" value={m.permissionMode ?? NONE} isDisabled={readOnly} options={{ [NONE]: "The team's setting", ...Object.fromEntries(MEMBER_PERMISSION_MODES.map((x) => [x, PERMISSION_WORDS[x] ?? x])) } as Record<string, string>} onChange={(x) => set({ permissionMode: x === NONE ? undefined : (x as OrgTeamMemberV2['permissionMode']) })} />
              </Row>
              <Row label="Subfolder" note={noteOf('cwd')} hint="Works inside this folder of the repository.">
                <input className={`${inputClass} max-w-64`} aria-label="Subfolder" value={m.cwd ?? ''} disabled={readOnly} onChange={(e) => set({ cwd: e.target.value || undefined })} placeholder="packages/api" />
              </Row>
            </>
          )}
          <Row label="Azure DevOps user" note={noteOf('azureDevOpsUser')} hint={m.human ? 'Lets work items assigned to them reach this person.' : undefined}>
            <input
              className={`${inputClass} max-w-64`}
              aria-label="Azure DevOps user"
              value={m.azureDevOpsUser?.uniqueName ?? ''}
              disabled={readOnly}
              onChange={(e) => set({ azureDevOpsUser: e.target.value ? { ...(m.azureDevOpsUser ?? {}), uniqueName: e.target.value } : undefined })}
              placeholder="name@company.com"
            />
          </Row>
        </div>
      )}

      {shownGroup === 'identity' && (
        <div className="grid grid-cols-1 gap-3">
          <Row label="Agent identity" hint={agentView?.error ? agentView.error : undefined}>
            {m.entraAgentId || agentView?.agentId ? (
              <span className="text-sm">
                {agentView?.displayName ? `${agentView.displayName} · ` : ''}
                <span className="mono">{agentView?.agentId ?? m.entraAgentId}</span>
              </span>
            ) : (
              <span className="text-sm text-muted">Registered with Microsoft Agent 365 when the team is saved.</span>
            )}
          </Row>
          <Row label="Sponsor">
            <span className="text-sm">{m.sponsor ?? <span className="text-muted">Set when the identity is made</span>}</span>
          </Row>
          <Row label="Sentinel workspace" hint={lookups.sentinel?.connected && lookups.sentinel.lastSentAt ? `Last telemetry sent ${timeAgo(lookups.sentinel.lastSentAt)}, for the whole organisation.` : undefined}>
            {lookups.sentinel ? <StatusMark state={lookups.sentinel.connected ? 'connected' : 'offline'} label={lookups.sentinel.connected ? `Sending to ${lookups.sentinel.stream ?? 'Sentinel'}` : 'Not connected'} /> : <span className="text-sm text-muted">Administrators see this.</span>}
          </Row>
          <Row label="Copilot access" hint="Whether Microsoft 365 Copilot may call this agent. The platform sets it.">
            <StatusMark state={m.a2a?.exposed ? 'connected' : 'disabled'} label={m.a2a?.exposed ? 'Copilot may call it' : 'Not offered to Copilot'} />
          </Row>
        </div>
      )}
    </div>
  );
}
